import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tabs,
  Tab,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import affiliateService from '../services/affiliateService';

function AffiliateRequests() {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState('view');
  const [adminNotes, setAdminNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadRequests(currentTab);
  }, [currentTab]);

  const loadRequests = async (status) => {
    try {
      setLoading(true);
      const data = await affiliateService.getRequests(status === 'all' ? null : status);
      setRequests(data.data || []);
      setFilteredRequests(data.data || []);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des demandes', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredRequests(requests);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = requests.filter(request => 
        request.user?.first_name?.toLowerCase().includes(term) ||
        request.user?.last_name?.toLowerCase().includes(term) ||
        request.user?.email?.toLowerCase().includes(term) ||
        request.companyName?.toLowerCase().includes(term) ||
        request.website?.toLowerCase().includes(term)
      );
      setFilteredRequests(filtered);
    }
  }, [searchTerm, requests]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleApprove = async () => {
    try {
      await affiliateService.approveRequest(selectedRequest.id, adminNotes);
      showSnackbar('Demande approuvée avec succès');
      setOpenDialog(false);
      loadRequests(currentTab);
    } catch (error) {
      showSnackbar('Erreur lors de l\'approbation', 'error');
    }
  };

  const handleReject = async () => {
    try {
      await affiliateService.rejectRequest(selectedRequest.id, adminNotes);
      showSnackbar('Demande rejetée');
      setOpenDialog(false);
      loadRequests(currentTab);
    } catch (error) {
      showSnackbar('Erreur lors du rejet', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const openRequestDialog = (request, action) => {
    setSelectedRequest(request);
    setDialogAction(action);
    setAdminNotes('');
    setOpenDialog(true);
  };

  const getStatusChip = (status) => {
    const config = {
      pending: { label: 'En attente', color: 'warning' },
      approved: { label: 'Approuvée', color: 'success' },
      rejected: { label: 'Rejetée', color: 'error' },
    };
    return <Chip label={config[status].label} color={config[status].color} size="small" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Demandes d'affiliation
      </Typography>

      {/* Barre de recherche */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher par nom, email, entreprise, site web..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: searchTerm && (
              <IconButton size="small" onClick={handleClearSearch}>
                <ClearIcon />
              </IconButton>
            ),
          }}
        />
      </Box>

      <Tabs value={currentTab} onChange={(e, value) => setCurrentTab(value)} sx={{ mb: 3 }}>
        <Tab label="En attente" value="pending" />
        <Tab label="Approuvées" value="approved" />
        <Tab label="Rejetées" value="rejected" />
        <Tab label="Toutes" value="all" />
      </Tabs>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Utilisateur</TableCell>
              <TableCell>Entreprise</TableCell>
              <TableCell>Site web</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    {searchTerm ? 'Aucun résultat trouvé' : 'Aucune demande'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRequests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {request.user?.first_name} {request.user?.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {request.user?.email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{request.companyName || '-'}</TableCell>
                <TableCell>{request.website || '-'}</TableCell>
                <TableCell>
                  {new Date(request.createdAt).toLocaleDateString('fr-FR')}
                </TableCell>
                <TableCell>{getStatusChip(request.status)}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => openRequestDialog(request, 'view')}>
                    <ViewIcon />
                  </IconButton>
                  {request.status === 'pending' && (
                    <>
                      <IconButton 
                        onClick={() => openRequestDialog(request, 'approve')}
                        color="success"
                      >
                        <ApproveIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => openRequestDialog(request, 'reject')}
                        color="error"
                      >
                        <RejectIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogAction === 'view' && 'Détails de la demande'}
          {dialogAction === 'approve' && 'Approuver la demande'}
          {dialogAction === 'reject' && 'Rejeter la demande'}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Utilisateur</Typography>
                <Typography>
                  {selectedRequest.user?.first_name} {selectedRequest.user?.last_name} ({selectedRequest.user?.email})
                </Typography>
              </Grid>
              
              {selectedRequest.companyName && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Entreprise</Typography>
                  <Typography>{selectedRequest.companyName}</Typography>
                </Grid>
              )}

              {selectedRequest.website && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Site web</Typography>
                  <Typography>{selectedRequest.website}</Typography>
                </Grid>
              )}

              {selectedRequest.socialMedia && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Réseaux sociaux</Typography>
                  <Typography>{selectedRequest.socialMedia}</Typography>
                </Grid>
              )}

              {selectedRequest.phone && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Téléphone</Typography>
                  <Typography>{selectedRequest.phone}</Typography>
                </Grid>
              )}

              {selectedRequest.motivation && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Motivation</Typography>
                  <Typography>{selectedRequest.motivation}</Typography>
                </Grid>
              )}

              {(dialogAction === 'approve' || dialogAction === 'reject') && (
                <Grid item xs={12}>
                  <TextField
                    label="Notes administrateur"
                    fullWidth
                    multiline
                    rows={3}
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Ajoutez une note (optionnel)..."
                  />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
          {dialogAction === 'approve' && (
            <Button onClick={handleApprove} variant="contained" color="success">
              Approuver
            </Button>
          )}
          {dialogAction === 'reject' && (
            <Button onClick={handleReject} variant="contained" color="error">
              Rejeter
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AffiliateRequests;


